<template>
    <div id="account" v-if="!this.$parent.loading && userCan('view_account_details', 'accounts')">
        <b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false">
            <b-icon icon="sync-alt"
                    custom-class="fa-spin">
            </b-icon>
            <p>Please wait while we gather the account data...</p>
        </b-loading>

        <template v-if="!loading && account">
            <b-message title="Limited Account Access" v-if="account.active_invite" type="is-warning" :closable="true" aria-close-label="Temporarily Hide This">
                {{ account.via_invite.invited_user.email }} was invited by {{ account.via_invite.invited_by }}. Access expires: {{ account.via_invite.expires_at.human }}.
            </b-message>

            <template v-if="!userCan('manage_simple_accounts', 'accounts') && account.type === 'Managed' && (account.status.state === 'uploading' || account.status.state === 'processing')">
                <template v-if="userCan('view_account_invites', 'account_invites') && userCan('update_accounts', 'accounts')">
                    <b-tabs type="is-boxed"
                            v-model="accountTabs"
                            @change="accountTabChanged"
                            :animated="true">
                        <b-tab-item label="Get Started" class="tab">
                            <div class="simple-accounts-options">
                                <h5 class="title is-5">How would you like to get the documents into this Griptiller account?</h5>
                                <p>If you need assistance, call Griptiller at 617-409-7500.</p>

                                <hr />

                                <div class="sa-option"
                                     :class="{'active' : fileRetrievalMethod === 'upload'}"
                                     v-on:click="changeFileRetrievalMethod('upload')"
                                >
                                    <b-tag rounded>1</b-tag>
                                    <p>I have the documents and I will <strong>upload</strong> them now.</p>
                                </div>

                                <div class="sa-option"
                                     :class="{'active' : fileRetrievalMethod === 'invite'}"
                                     v-on:click="changeFileRetrievalMethod('invite')"
                                >
                                    <b-tag rounded>2</b-tag>
                                    <p>Another party has the documents, and I'd like to <strong>invite</strong> them to upload the documents.</p>
                                </div>
                                <transition name="animateHeight" mode="out-in" duration="500">
                                    <div class="sa-option-help" v-if="fileRetrievalMethod === 'invite'">
                                        <p>Invited users will receive an email from Griptiller with an easy drag and drop link for them to upload the documents.</p>

                                        <account-access :simple="true" />

                                        <b-button
                                                tag="router-link"
                                                type="is-white"
                                                size="is-small"
                                                :to="{ 'name': 'account-invites-create', query: { 'search': 'upload_only' } }">
                                            Create Invite
                                        </b-button>
                                    </div>
                                </transition>

                                <div class="sa-option"
                                     :class="{'active' : fileRetrievalMethod === 'assisted'}"
                                     v-on:click="changeFileRetrievalMethod('assisted')"
                                >
                                    <b-tag rounded>3</b-tag>
                                    <div class="pull-right">
                                        <b-tooltip position="is-left" type="is-info" label="You email your documents to Griptiller.">
                                            <b-icon icon="question-circle" />
                                        </b-tooltip>
                                    </div>
                                    <p>I have a data link to the documents and I'd like <strong>Griptiller</strong> to retrieve and upload the documents for me.</p>
                                </div>
                                <transition name="animateHeight" mode="out-in" duration="500">
                                    <div class="sa-option-help" v-if="fileRetrievalMethod === 'assisted'">
                                        <p>Please email the document links to Griptiller (<a :href="`mailto:admin@griptiller.com?subject=Documents for: ` + account.name.value">admin@griptiller.com</a>).</p>
                                    </div>
                                </transition>

                            </div>
                        </b-tab-item>
                        <b-tab-item label="Upload Documents" class="tab">
                            <template slot="header">
                                <b-tag rounded>{{ account.files.total }}</b-tag>
                                Upload Documents
                            </template>
                            <simple-account-uploader
                                    :account="account"
                                    v-on:managed-status-changed="handleManagedStatusUpdate"
                                    v-if="userCan('upload_account_files', 'account_files')" />
                        </b-tab-item>
                    </b-tabs>
                </template>
                <template v-else>
                    <div class="box">
                        <simple-account-uploader
                                :account="account"
                                v-on:managed-status-changed="handleManagedStatusUpdate"
                                v-if="userCan('upload_account_files', 'account_files')" />
                    </div>
                </template>
            </template>
            <template v-else>
                <div v-if="canViewHeadmast"
                     class="account-headmast"
                     :style="'border-left: 30px solid ' + account.status.color">
                    <div class="account-headmast-top">
                        <div class="columns is-tablet">
                            <div class="column account-headmast-top-details">
                                <div class="columns is-desktop" v-if="account.type === 'Standard'">
                                    <div class="column is-4-desktop" v-if="userCan('view_account_broker', 'accounts')">
                                        <div class="title is-5">
                                            {{ account.broker }}
                                            <span>Broker</span>
                                        </div>
                                    </div>
                                    <div class="column is-4-desktop" v-if="userCan('view_account_carrier', 'accounts')">
                                        <div class="title is-5">
                                            {{ account.carrier }}
                                            <span>Carrier</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="columns is-desktop" v-if="account.type === 'Managed'">
                                    <div class="column" :class="topRowSubColumnClass">
                                        <div class="title is-5">
                                            {{ account.created_by }}
                                            <span>Managed By</span>
                                        </div>
                                    </div>
                                    <div class="column" :class="topRowSubColumnClass">
                                        <div class="title is-5">
                                            {{ account.company.name }}
                                            <span>Company</span>
                                        </div>
                                    </div>
                                    <div class="column" :class="topRowSubColumnClass" v-if="!userFromShareLink && account.account_number">
                                        <div class="title is-5">
                                            {{ account.account_number }}
                                            <span>Account #</span>
                                        </div>
                                    </div>
                                    <div class="column" :class="topRowSubColumnClass" v-if="!userFromShareLink && !account.account_number">
                                        <div class="title is-5">
                                            {{ account.updated_at }}
                                            <span>Last Updated On</span>
                                        </div>
                                    </div>
                                    <div class="column" :class="topRowSubColumnClass" v-if="!userFromShareLink">
                                        <div class="title is-5">
                                            {{ account.created_at }}
                                            <span>Created On</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="column account-headmast-top-status" :class="topRowColumnClass"
                                 v-if="userCan('view_account_status', 'accounts') && userCan('manage_simple_accounts', 'accounts')">
                                <div class="account-status">
                                    <b-dropdown position="is-bottom-left" :disabled="!userCan('update_account_status', 'accounts')">
                                        <button slot="trigger" class="button"
                                                :class="fontColor(account.status.color)"
                                                :style="'background-color: ' + account.status.color">
                                            <span class="button-label">{{ account.status.name }}</span>
                                            <b-icon icon="caret-down" v-if="userCan('update_accounts', 'accounts')"></b-icon>
                                        </button>

                                        <b-dropdown-item
                                                v-for="status in account.statuses" :key="status.id"
                                                v-on:click="changeStatus(status)"
                                                :class="{ 'is-selected': status.name === account.status }">
                                            <span class="status-icon"><i class="fas fa-circle" :style="'color: ' + status.color"></i></span>
                                            <span class="status-name">{{ status.name }}</span>
                                        </b-dropdown-item>
                                    </b-dropdown>
                                </div>
                                <favorite model="accounts" :model-id="account.id" :favorite="account.favorite" v-on:change="getAccount" />
                            </div>

                            <div class="column account-headmast-top-status-fake" :class="topRowColumnClass"
                                 v-else-if="userCan('view_account_status', 'accounts')">
                                <div class="account-status">
                                    <button class="button is-fake has-text-centered"
                                            :class="fontColor(account.status.color)"
                                            :style="'background-color: ' + account.status.color">
                                        <span class="button-label">{{ account.status.name }}</span>
                                    </button>
                                </div>
                                <favorite v-if="!userFromInviteLink" model="accounts" :model-id="account.id" :favorite="account.favorite" v-on:change="getAccount" />
                            </div>

                            <div class="column account-headmast-top-downloads" :class="topRowColumnClass" v-if="userFromShareLink || userFromInviteLink">
                                <account-downloads :account-id="account.id" :account-name="account.name.value" :account-number="account.account_number" :file-size="account.files.size.total" />
                            </div>
                        </div>
                    </div>

                    <div class="account-headmast-details" v-if="!userFromShareLink">
                        <template v-if="account.type === 'Managed'">
                            <div class="account-share-wrapper account-data-cell" v-if="userCan('update_account_sharing', 'accounts')">
                                <account-share-form :account="account"></account-share-form>
                            </div>
                            <account-downloads :account-id="account.id" :account-name="account.name.value" :account-number="account.account_number" v-if="!userFromShareLink" :file-size="account.files.size.total" />
                        </template>
                        <template v-if="account.type === 'Standard'">
                            <div class="account-data-cell" v-if="account.account_number">
                                {{ account.account_number }}
                                <span>Account #</span>
                            </div>
                            <div class="account-data-cell" v-if="account.effective_date">
                                {{ account.effective_date }}
                                <span>Effective Date</span>
                            </div>
                            <div class="account-data-cell"
                                 v-if="hasAccountMeta"
                                 v-for="(row, index) in account.meta"
                                 :key="index">
                                {{ row.value }}
                                <span>{{ row.label }}</span>
                            </div>
                            <div class="account-data-cell has-text-right" v-if="account.created_at">
                                {{ account.created_at }}
                                <span>Created At</span>
                            </div>
                        </template>
                    </div>
                </div>

                <b-tabs type="is-boxed"
                        :animated="false"
                        v-model="accountTabs"
                        @change="accountTabChanged">
                    <div id="faux-tabs">
                      <div id="account-overview-tab" class="faux-tab" v-if="canViewOverview"></div>
                      <div id="account-properties-tab" class="faux-tab" v-if="userCan('view_account_properties', 'account_properties')"></div>
                      <div id="account-files-tab" class="faux-tab" v-if="userCan('view_account_files', 'account_files')"></div>
                      <div id="account-access-tab" class="faux-tab" v-if="userCan('view_account_invites', 'account_invites')"></div>
                    </div>
                    <b-tab-item label="Overview" class="tab" v-if="canViewOverview">
                        <template slot="header">
                          <span class="tab-text-container" id="account-overview-tab-text">Overview</span>
                        </template>
                        <div class="columns is-desktop" v-if="tabsLoaded.includes(0) || accountTabs === 0">
                            <div class="column" :class="{'is-9-desktop' : showSidebar}">
                                <div class="columns">
                                    <div class="column is-4 pie-metric-wrapper"
                                         v-if="userCan('view_property_types_pie_metric', 'accounts')">
                                        <pie-metric
                                                metricSize="is-12"
                                                :isParent="false"
                                                :metric="'accounts/' + account.id + '/property-types'" />
                                    </div>
                                    <div class="column">
                                        <h4 class="title is-4" v-if="!userCan('view_property_types_pie_metric', 'accounts')">Properties Map</h4>
                                        <div class="account-properties-map">
                                            <b-loading :is-full-page="false" :active.sync="mapMarkers.length === 0 && mapLoaded === false" :can-cancel="false">
                                                <b-icon
                                                        pack="fas"
                                                        icon="sync-alt"
                                                        custom-class="fa-spin">
                                                </b-icon>
                                                <p>Please wait while we gather the map data...</p>
                                            </b-loading>
                                            <b-notification
                                                    v-if="mapMarkers.length === 0 && mapLoaded === true"
                                                    class="no-properties-map-notice"
                                                    :closable="false">
                                                <div class="has-text-centered">
                                                    <h3 class="subtitle is-5">No properties exist on this account.</h3>
                                                    <b-button v-if="userCan('create_account_properties', 'account_properties')"
                                                              type="is-primary"
                                                              icon-left="plus"
                                                              tag="router-link"
                                                              :to="{path: `/accounts/${account.id}/properties/create`}">
                                                        Create your first property
                                                    </b-button>
                                                    <b-button v-if="userCan('bulk_upload_account_properties', 'account_properties')"
                                                              type="is-info"
                                                              icon-left="upload"
                                                              tag="router-link"
                                                              :to="{path: `/accounts/${account.id}/import`}">
                                                        Import multiple properties
                                                    </b-button>
                                                </div>
                                            </b-notification>
                                            <google-maps v-if="mapMarkers.length > 0"
                                                         infoWindowTemplate="property"
                                                         :map-zoom="5"
                                                         :height="650"
                                                         :markers="mapMarkers">
                                            </google-maps>
                                        </div>
                                    </div>
                                </div>
                                <div class="columns is-desktop"
                                     v-if="userCan('view_account_reconciliation_metric', 'accounts')">
                                    <div class="column" id="reconciliation-metrics">
                                        <counts-metric
                                                metricSize="is-12"
                                                :isParent="false"
                                                :metric="'accounts/' + account.id + '/reconciliation'"></counts-metric>
                                    </div>
                                </div>
                                <div v-if="userCan('view_property_types__metric', 'accounts') || userCan('view_property_locations_metric', 'accounts')">
                                    <div class="columns is-desktop">
                                        <div class="column" v-if="userCan('view_property_types_metric', 'accounts')">
                                            <doughnut-metric
                                                    metricSize="is-12"
                                                    :isParent="false"
                                                    :metric="'accounts/' + account.id + '/property-types'"></doughnut-metric>
                                        </div>
                                        <div class="column" v-if="userCan('view_property_locations_metric', 'accounts')">
                                            <doughnut-metric
                                                    metricSize="is-12"
                                                    :isParent="false"
                                                    :metric="'accounts/' + account.id + '/property-locations'"></doughnut-metric>
                                        </div>
                                    </div>
                                </div>
                                <template v-if="!showSidebar">
                                    <div class="account-actions m-t-30">
                                        <b-button type="is-info"
                                                  size="is-small"
                                                  v-if="userCan('update_accounts', 'accounts')"
                                                  tag="router-link"
                                                  :to="{path: `/accounts/${account.id}/edit-overview`}">
                                            Edit Account
                                        </b-button>
                                        <b-button v-on:click="deleteAccount"
                                                  v-if="userCan('delete_accounts', 'accounts')"
                                                  size="is-small"
                                                  type="is-danger">
                                            Delete Account
                                        </b-button>
                                    </div>
                                </template>
                            </div>
                            <div class="column is-3-desktop" v-if="showSidebar">
                                <div class="account-sidebar">
                                    <div class="account-user" v-if="account.invite || account.website">
                                        <template v-if="account.invite">
                                            <h6 class="title is-6">Invited By</h6>
                                            <p>{{ account.invite.created_by_formatted }}<br/>
                                                {{ account.invite.created_by_user.email }}<br/>
                                                {{ account.invite.created_by_user.cell_phone }}</p>
                                        </template>

                                        <p v-if="account.website" class="m-t-20">
                                            <a :href="account.website" target="_blank">
                                                View Account Website
                                                <b-icon icon="external-link-alt" class="m-l-5" size="is-small" style="vertical-align: sub"></b-icon>
                                            </a>
                                        </p>
                                    </div>

                                    <template v-if="account.type === 'Standard' && !userFromInviteLink">
                                        <account-downloads :account-id="account.id" :account-name="account.name.value" :account-number="account.account_number" :file-size="account.files.size.total" />
                                    </template>

                                    <div class="account-timeline" v-if="userCan('view_account_activity', 'accounts')">
                                        <h5 class="title is-5">Recent Activity</h5>
                                        <recent-activity ref="recentActivity" :scrollActive="this.accountTabs === 0" :account="account"></recent-activity>
                                    </div>

                                    <div class="account-actions">
                                        <b-button type="is-info"
                                                  size="is-small"
                                                  v-if="userCan('update_accounts', 'accounts')"
                                                  tag="router-link"
                                                  :to="{path: `/accounts/${account.id}/edit-overview`}">
                                            Edit Account
                                        </b-button>
                                        <b-button v-on:click="deleteAccount"
                                                  v-if="userCan('delete_accounts', 'accounts')"
                                                  size="is-small"
                                                  type="is-danger">
                                            Delete Account
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-tab-item>
                    <b-tab-item v-if="userCan('view_account_properties', 'account_properties')">
                        <template slot="header">
                          <span id="properties-tab-text">
                            <span style="padding-right: 15px;">Properties:</span>
                            <b-tag rounded>{{ account.properties }}</b-tag>
                            <span style="padding-right: 15px;">Active</span>
                            <b-tag rounded>{{ account.divested_properties }}</b-tag>
                            <span>Divested</span>
                          </span>
                        </template>

                        <account-properties :account="account" v-if="tabsLoaded.includes(1) || accountTabs === 1" />
                    </b-tab-item>
                    <b-tab-item v-if="userCan('view_account_files', 'account_files')">
                        <template slot="header">
                          <span id="files-tab-text">
                            <b-tag rounded>{{ account.files.total }}</b-tag>
                            Documents
                          </span>
                        </template>

                        <b-tabs type="is-boxed" v-if="tabsLoaded.includes(2) || accountTabs === 2">
                            <b-tab-item>
                                <template slot="header">
                                    <b-tag rounded>{{ account.files.disclosed }}</b-tag>
                                    <b-tag v-if="account.files.disclosed" rounded class="data">{{ account.files.size.disclosed }}</b-tag>
                                    Disclosed Documents
                                </template>
                                <account-files
                                  :show-vanity-by-default="account.show_vanity_by_default"
                                  :account-name="account.name.value"
                                  :filters="{'filter__sov': 0}" />
                            </b-tab-item>
                            <b-tab-item>
                                <template slot="header">
                                    <b-tag rounded>{{ account.files.sov }}</b-tag>
                                    <b-tag v-if="account.files.sov" rounded class="data">{{ account.files.size.sov }}</b-tag>
                                    Submission Documents
                                </template>
                                <account-files
                                  :account-name="account.name.value"
                                  :show-vanity-by-default="account.show_vanity_by_default"
                                  :filters="{'filter__sov': 1}"
                                  :show-disclosed-documents-download="false"
                                  :show-properties="false" />
                            </b-tab-item>
                        </b-tabs>
						<div v-if="account.send_new_files_notification" class="text-right text-grey">
							<p><em>New file notifications are enabled. Users with permission will receive alerts.</em></p>
						</div>
                    </b-tab-item>
                    <b-tab-item v-if="userCan('view_account_invites', 'account_invites')">
                        <template slot="header">
                          <span>
                            <b-tag rounded>{{ account.users }}</b-tag>
                            <span class="is-hidden-mobile">Account</span> Access
                          </span>
                        </template>
                        <account-access v-if="tabsLoaded.includes(3) || accountTabs === 3" />
                    </b-tab-item>
                </b-tabs>
            </template>
        </template>
        <router-view/>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapMutations, mapGetters } from 'vuex'
import {
  ACCOUNT_GET,
  ACCOUNT_DELETE,
  ACCOUNT_STATUS_SET,
  ACCOUNT_PUT,
  ACCOUNT_SHOW_TOUR
} from '../../store/actions/accounts'
import { ACCOUNT_PROPERTY_MAP } from '../../store/actions/properties'
import { ACTIVITY_GET } from '../../store/actions/activity'
import DoughnutMetric from '../Metrics/DoughnutMetric'
import PieMetric from '../Metrics/PieMetric'
import CountsMetric from '../Metrics/CountsMetric'
import AccountFiles from './Tabs/AccountFiles'
import AccountProperties from './Tabs/AccountProperties'
import AccountAccess from './Tabs/AccountAccess'
import fontColor from '@/functions/fontColor'
import GoogleMaps from '../Elements/GoogleMaps'
import SimpleAccountUploader from './SimpleAccountUploader'
import AccountShareForm from './AccountShareForm'
import AccountDownloads from './AccountDownloads'
import RecentActivity from './RecentActivity'
import Favorite from '@/components/Elements/Favorite'

export default {
  name: 'AccountDash',
  components: {
    GoogleMaps,
    DoughnutMetric,
    PieMetric,
    CountsMetric,
    AccountFiles,
    AccountProperties,
    AccountAccess,
    SimpleAccountUploader,
    AccountShareForm,
    AccountDownloads,
    RecentActivity,
    Favorite
  },
  data () {
    return {
      id: this.$route.params.id,
      account: {},
      accountsRow: null,
      accountActivity: false,
      loading: true,
      accountTabs: null,
      fileRetrievalMethod: null,
      mapMarkers: [],
      mapLoaded: false,
      componentKey: 0,
      tabsLoaded: []
    }
  },
  computed: {
    ...mapState({
      init: state => state.init.all,
      accounts: state => state.accounts.recent,
      modelUpdatedAt: state => state.modelUpdatedAt,
      dataChanged: state => state.dataChanged,
      accountActiveTabs: state => state.accountActiveTabs
    }),
    ...mapGetters({
      pusher: 'pusher/init',
      userCan: 'userCan',
      userFromShareLink: 'isFromShareLink',
      userFromInviteLink: 'isFromInviteLink'
    }),
    pusherChannel: function () {
      if (!this.pusher) return
      return this.pusher.subscribe('private-account.' + this.id)
    },
    href () {
      return '#' + this.name.toLowerCase().replace(/ /g, '-')
    },
    hasAccountMeta () {
      return Object.keys(this.account.meta).length > 0
    },
    invitesParams () {
      return {
        account_id: this.$route.params.id
      }
    },
    showSidebar () {
      if (this.account.invite || this.account.website) return true
      if (this.userCan('view_account_activity', 'accounts')) return true
      if (this.userFromInviteLink) return false
      if (this.account.type === 'Standard') return true

      return false
    },
    topRowColumnClass () {
      if (this.account.type === 'Standard' && !this.userFromShareLink && !this.userFromInviteLink) return 'is-4-desktop is-3-tablet'
      if (!this.userFromShareLink && !this.userFromInviteLink) return 'is-2-desktop is-3-tablet'
      return 'is-3-desktop'
    },
    topRowSubColumnClass () {
      if (this.userFromShareLink) return 'is-4-desktop is-12-tablet'
      return 'is-3-desktop'
    },
    canViewOverview () {
      if (this.userCan('view_property_types_pie_metric', 'accounts')) return true
      if (this.userCan('create_account_properties', 'account_properties')) return true
      if (this.userCan('bulk_upload_account_properties', 'account_properties')) return true
      if (this.userCan('view_account_reconciliation_metric', 'accounts')) return true
      if (this.userCan('view_property_types__metric', 'accounts')) return true
      if (this.userCan('view_property_locations_metric', 'accounts')) return true

      if (this.showSidebar) {
        if (this.userCan('update_accounts', 'accounts')) return true
        if (this.userCan('delete_accounts', 'accounts')) return true
      }

      if (!this.userCan('manage_simple_accounts', 'accounts') && this.account.type === 'Managed' && (this.account.status.state === 'uploading' || this.account.status.state === 'processing')) {
        if (this.userCan('view_account_invites', 'account_invites') && this.userCan('update_accounts', 'accounts')) return true
      }

      return false
    },
    canViewHeadmast () {
      if (this.userCan('view_account_broker', 'accounts')) return true
      if (this.userCan('view_account_carrier', 'accounts')) return true
      if (this.userCan('view_account_status', 'accounts') && this.userCan('manage_simple_accounts', 'accounts')) return true

      if (this.userCan('view_download_document_list_button', 'accounts')) return true
      if (this.userCan('view_download_property_list_button', 'accounts')) return true
      if (this.userCan('view_download_all_files_button', 'accounts')) return true

      return false
    },
    downloadsTab () {
      // used to determine which tab number the files are on
      let tabNum = 2

      if (!this.canViewOverview) tabNum--
      if (!this.userCan('view_account_properties', 'account_properties')) tabNum--

      return tabNum
    }
  },
  watch: {
    modelUpdatedAt: function () {
      this.getAccount()

      if (this.dataChanged === 'data' || (_.isObject(this.dataChanged) && this.dataChanged.type === 'data')) {
        if (this.$refs.recentActivity) {
          this.$refs.recentActivity.addActivity()
        }
        this.getPropertiesMap()
      }

      if (this.dataChanged === 'files') {
        if (this.$refs.recentActivity) {
          this.$refs.recentActivity.addActivity()
        }
      }
    },
    modalIsExpanded: function () {
      if (this.modalIsExpanded === false) {
        this.$router.push({ params: { id: this.id } })
      }
    },
    '$route.params.id': function (id) {
      this.id = id
      this.initializeData()
    },
    accountTabs: function (newVal, oldVal) {
      if (oldVal === null) return
      if (newVal === oldVal) return
      if (this.loading === true) return
      if (this.tabsLoaded.includes(oldVal)) return
      this.tabsLoaded.push(oldVal)
    },
    accountActiveTabs: {
      handler: function () {
        this.setActiveTab()
      },
      deep: true
    },
    tabsLoaded: function () {
      if (!this.tabsLoaded.includes(0) && this.accountTabs === 0) {
        this.getPropertiesMap()
        if (this.$refs.recentActivity) {
          this.$refs.recentActivity.addActivity()
        }
      }
    },
    fileRetrievalMethod: function (newVal, oldVal) {
      if (this.account.type !== 'Managed') return

      if (oldVal === null && (newVal === 'invite' || newVal === 'upload')) {
        if (!this.canViewOverview) {
          this.accountTabs = this.downloadsTab
          return
        }

        if (this.account.files.total > 0) {
          this.accountTabs = 1
        }
      }
    }
  },
  methods: {
    ...mapMutations([
      'setPageTitle'
    ]),
    fontColor: fontColor,
    handleError (err) {
      if (err.hasOwnProperty('response')) {
        if (err.response.status === 404 || err.response.status === 403) {
          // going back to many times
          this.$router.push('/accounts')
          this.$emit('modal-close')
        }

        if (err.response.data.response === 'error') {
          this.$awn.warning(err.response.data.message)
        }
      } else {
        this.$awn.alert(err.toString())
      }
    },
    updatePageHeader () {
      // This is here in case the user navigates to multiple accounts too quickly
      if (this.$route.name !== undefined && !this.$route.name.includes('account')) return
      if (this.$route.params.id !== this.id) return
      if (!this.account) return

      this.setPageTitle(this.account.name.value)
    },
    setActiveTab () {
      if (this.accountActiveTabs[this.account.id]) {
        this.accountTabs = this.accountActiveTabs[this.account.id]
      } else if (!this.canViewOverview) {
        this.accountTabs = this.downloadsTab
        this.tabsLoaded.push(2)
      } else {
        this.accountTabs = 0
        this.getPropertiesMap()
      }
    },
    getAccountRowFromAccounts () {
      for (let account in this.accounts) {
        if (!this.accounts.hasOwnProperty(account)) continue

        if (this.accounts[account].id.toString() === this.id) {
          return account
        }
      }

      return null
    },
    getAccount () {
      this.accountsRow = this.getAccountRowFromAccounts()
      if (this.accounts && this.accountsRow) {
        this.account = this.accounts[this.accountsRow]
        this.updatePageHeader()
        this.setActiveTab()
        this.loading = false
      }

      this.$store.dispatch(ACCOUNT_GET, this.id).then(response => {
        this.account = response.data
        this.$store.dispatch('accounts/setAccount', {
          row: this.accountsRow,
          account: this.account
        })

        if (!_.isNull(response.data.invite)) {
          this.$store.dispatch('setInvite', {
            full_name: response.data.invite.created_by_formatted,
            company: response.data.invite.created_by_user.company.name,
            date: response.data.invite.created_at_formatted,
            logo_url: response.data.company.logo_url
          })
        }

        if (!_.isNull(response.data.file_retrieval_method) && this.fileRetrievalMethod === null) {
          this.fileRetrievalMethod = response.data.file_retrieval_method
        }

        this.updatePageHeader()
        this.setActiveTab()
        this.loading = false
        this.$store.dispatch(ACCOUNT_SHOW_TOUR, response.data).then(() => {
          this.$nextTick(function () {
            const overview = document.getElementById('account-overview-tab-text')
            const overviewTab = document.getElementById('account-overview-tab')
            if (overview && overviewTab) {
              overviewTab.style.width = overview.closest('li').offsetWidth + 'px'
            }

            const properties = document.getElementById('properties-tab-text')
            const propertiesTab = document.getElementById('account-properties-tab')
            if (properties && propertiesTab) {
              propertiesTab.style.width = properties.closest('li').offsetWidth + 'px'
            }

            const documents = document.getElementById('files-tab-text')
            const documentsTab = document.getElementById('account-files-tab')
            if (documents && documentsTab) {
              documentsTab.style.width = documents.closest('li').offsetWidth + 'px'
            }
          })
        })
      }).catch(err => {
        this.handleError(err)
      })
    },
    async getPropertiesMap () {
      if (!this.userCan('view_account_properties', 'account_properties')) return

      this.$store.dispatch(ACCOUNT_PROPERTY_MAP, this.id).then(response => {
        this.mapMarkers = response.data
        this.mapLoaded = true
      }).catch(err => {
        this.handleError(err)
      })
    },
    async getActivity () {
      this.$store.dispatch(ACTIVITY_GET, {
        model: 'accounts',
        modelId: this.id
      }).then(response => {
        this.accountActivity = response.data
      }).catch(err => {
        this.handleError(err)
      })
    },
    changeStatus (status) {
      this.account.status = status

      const accountId = this.id
      const statusId = status.id

      this.$store.dispatch(ACCOUNT_STATUS_SET, { accountId, statusId }).then(response => {
        this.getAccount()

        this.$store.dispatch('accounts/setAccount', {
          row: this.accountsRow,
          account: this.account
        })

        this.$awn.success(response.message)

        this.$store.commit('modelUpdated')
      }).catch(err => {
        this.handleError(err)
      })
    },
    deleteAccount () {
      this.$buefy.dialog.confirm({
        title: 'Confirm Deletion',
        message: 'Are you sure you want to <stronge>delete</strong> this record? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch(ACCOUNT_DELETE, this.id).then(response => {
            this.$awn.success(response.message)
            this.$router.push({ name: 'accounts' })
          }).catch(err => {
            this.handleError(err)
          })
        }
      })
    },
    accountTabChanged (tab) {
      this.$store.commit('setAccountActiveTab', {
        'account_id': this.account.id,
        'tab': tab
      })
    },
    initializeData () {
      this.loading = true

      this.setPageTitle('Accounts')

      this.getAccount()
    },
    handleManagedStatusUpdate (newStatus) {
      this.account.status.state = newStatus
      this.account.status.name = 'Processing'
    },
    changeFileRetrievalMethod (selection) {
      if (this.fileRetrievalMethod === selection && selection === 'upload') {
        this.accountTabs = 1
      }

      if (this.fileRetrievalMethod === null && selection === 'upload') {
        this.accountTabs = 1
      }

      if (this.fileRetrievalMethod !== selection && selection === 'assisted') {
        this.simpleAccountConfirm()
        return
      }

      if (this.fileRetrievalMethod === 'assisted' && selection !== 'assisted') {
        this.simpleAccountCancel(selection)
        return
      }

      this.fileRetrievalMethod = selection
      this.updateRetrievalMethod(selection)
    },
    simpleAccountConfirm () {
      // Confirm enable and disable
      this.$buefy.dialog.confirm({
        message: 'Please confirm that you would like Griptiller to retrieve and upload the documents for you. This will notify Griptiller immediately.',
        cancelText: 'No, thanks',
        confirmText: 'Yes',
        type: 'is-info',
        hasIcon: true,
        onConfirm: () => {
          this.$awn.success('Awesome! We will contact you soon to collect documents.')
          this.fileRetrievalMethod = 'assisted'
          this.updateRetrievalMethod('assisted')
        }
      })
    },
    simpleAccountCancel (selection) {
      // Confirm enable and disable
      this.$buefy.dialog.confirm({
        message: 'Would you like to cancel your request for Griptiller to retrieve and upload the documents for you?',
        cancelText: 'No, thanks',
        confirmText: 'Yes',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: () => {
          this.$awn.success('No problem! We will wait for you to upload your documents.')
          this.fileRetrievalMethod = selection
          this.updateRetrievalMethod(selection)
        }
      })
    },
    updateRetrievalMethod (fileRetrievalMethod) {
      this.$store.dispatch(ACCOUNT_PUT, {
        'account_id': this.id,
        'file_retrieval_method': fileRetrievalMethod
      }).then(() => {
        this.account.file_retrieval_method = fileRetrievalMethod
      }).catch(err => {
        this.handleError(err)
      })
    }
  },
  created () {
    this.initializeData()
    this.$store.commit('initDatatableState', 'account_properties')
    this.$store.commit('initDatatableState', 'account_files')
    this.$store.commit('setUnderModalLink', null)
  },
  mounted () {
    if (this.pusherChannel) {
      this.pusherChannel.bind('model-updated', (data) => {
        let local = new Date(this.modelUpdatedAt)
        let pusher = new Date(data.updatedAt)

        if (pusher.getTime() > local.getTime()) {
          this.getAccount()
        }
      })
    }
  },
  beforeDestroy () {
    if (this.pusher) {
      this.pusher.unsubscribe('private-account.' + this.id)
    }
    if (this.pusherChannel) {
      this.pusherChannel.unbind('model-updated')
    }
  }
}
</script>

<style lang="scss">
.account-status .dropdown.is-disabled {
  opacity: 1 !important;
  cursor: pointer !important;
}

.tabs li a > span {
  position: relative;
}

span.tab-text-container {
  display: inline;
}

#faux-tabs {
    position: absolute;
    height: 55px;
    width: 100%;
    z-index: -1;
    top: -56px;
    left: 0;
    .faux-tab {
        width: 25%;
        display: inline-block;
        height: 55px;
        margin-right: -6px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Account/Account.vue